import React, { useMemo } from "react";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import {
  AddAlarm,
  CorporateFare,
  Dashboard,
  Description,
  Fence,
  Home,
  Person,
  Router,
  Settings,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";

function SidebarLayout({ collapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation("common");

  // Memoize menu styles with modern glassmorphic design
  const menuItemStyles = useMemo(
    () => ({
      button: ({ active }) => ({
        color: active
          ? theme.palette.primary.main
          : theme.palette.mode === "dark"
            ? theme.palette.grey[300]
            : theme.palette.grey[700],
        backgroundColor: active
          ? theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.primary.main, 0.08)
          : "transparent",
        borderRadius: collapsed ? "10px" : "0 24px 24px 0",
        marginRight: collapsed ? "10px" : "12px",
        marginLeft: collapsed ? "10px" : "0",
        marginTop: "4px",
        marginBottom: "4px",
        padding: collapsed ? "10px" : undefined,
        justifyContent: collapsed ? "center" : undefined,
        position: "relative",
        transition: "all 0.2s ease",
        fontWeight: active ? 600 : 500,
        fontSize: "0.9rem",
        // Active indicator bar - only show when expanded
        "&::before":
          active && !collapsed
            ? {
                content: '""',
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: "3px",
                height: "60%",
                borderRadius: "0 4px 4px 0",
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }
            : {},
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.primary.main, 0.15)
              : alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
        },
      }),
      icon: ({ active }) => ({
        color: active
          ? theme.palette.primary.main
          : theme.palette.mode === "dark"
            ? theme.palette.grey[400]
            : theme.palette.grey[600],
        transition: "color 0.2s ease",
        marginRight: collapsed ? 0 : undefined,
      }),
      label: () => ({
        opacity: collapsed ? 0 : 1,
        transition: "opacity 0.2s ease",
      }),
    }),
    [theme.palette, collapsed]
  );

  // Memoize sidebar styles
  const sidebarRootStyles = useMemo(
    () => ({
      borderRightWidth: 0,
      "& .ps-sidebar-container": {
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`
            : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f5f5f5", 0.95)} 100%)`,
        backdropFilter: "blur(10px)",
        borderRight: `1px solid ${
          theme.palette.mode === "dark"
            ? alpha(theme.palette.divider, 0.1)
            : alpha(theme.palette.divider, 0.15)
        }`,
      },
    }),
    [theme.palette]
  );

  // Define menu items configuration
  const menuItems = useMemo(
    () => [
      { path: "/", icon: <Home />, label: "common.home" },
      { path: "/device", icon: <Router />, label: "common.devices" },
      { path: "/action", icon: <AddAlarm />, label: "common.actions" },
      { path: "/geofence", icon: <Fence />, label: "common.geofences" },
      { path: "/dashboard", icon: <Dashboard />, label: "common.dashboards" },
      { path: "/report", icon: <Description />, label: "common.reports" },
      { path: "/contact", icon: <Person />, label: "common.contacts" },
      {
        path: "/organisation",
        icon: <CorporateFare />,
        label: "common.organisations",
      },
      { path: "/account", icon: <Settings />, label: "common.account" },
    ],
    []
  );

  return (
    <Sidebar
      collapsed={collapsed}
      className="app"
      rootStyles={sidebarRootStyles}
    >
      <Menu menuItemStyles={menuItemStyles}>
        {menuItems.map((item, index) => (
          <MenuItem
            key={"sidebar" + index}
            onClick={() => navigate(item.path)}
            icon={item.icon}
            active={location.pathname === item.path}
          >
            {t(item.label)}
          </MenuItem>
        ))}
      </Menu>
    </Sidebar>
  );
}

export default React.memo(SidebarLayout);
